import * as React from 'react';
import classNames from 'classnames';
import {FocusTrap} from 'components/FocusTrap';
import {ImageOptions, Picture} from 'components/UIKit/Picture';
import {Icon} from 'components/UIKit/Icon';
import {NOOP} from 'components/UIKit/utilities';
import {ShopScreenShot} from '../../../../types';
import style from './image-viewer.scss';

export interface ImageViewerProps {
  altPrefix?: string;
  images: ShopScreenShot[];
  showing?: boolean;
  onClose?: () => void;
  selected: ShopScreenShot;
  infinite?: boolean;
  onImageSelect?: (selectedImage: ShopScreenShot) => void;
}

const fullImageOptions: ImageOptions[] = [
  {
    breakpoint: {
      min: null,
      max: 750,
    },
    options: {
      format: 'jpg',
      width: 345,
      height: 223,
    },
  },
  {
    breakpoint: {
      min: 751,
      max: 1080,
    },
    fallback: true,
    options: {
      format: 'jpg',
      width: 648,
      height: 904,
    },
  },
  {
    breakpoint: {
      min: 1081,
      max: null,
    },
    options: {
      format: 'jpg',
      width: 1044,
      height: 1080,
    },
  },
];

function getIndices(list: ShopScreenShot[], selected: ShopScreenShot, infinite = false) {
  const active = list.findIndex((image) => image.id === selected.id);
  const last = list.length - 1;

  const isLast = active === last;
  const isFirst = active === 0;

  let prev: number | null = isFirst ? null : active - 1;
  let next: number | null = isLast ? null : active + 1;

  if (infinite && list.length > 1) {
    next = next === null ? 0 : next;
    prev = prev === null ? last : prev;
  }

  const indices = {
    prev,
    next,
    active,
    displayIndex: `${active + 1}/${list.length}`,
  };

  return {
    ...indices,
    isLast,
    isFirst,
    prevImage: indices.prev !== null && list[indices.prev],
    nextImage: indices.next !== null && list[indices.next],
  };
}

const ICON_SIZE = 24;
const ICON_FILL = '#f7f7f8';

const ESCAPE = 'Escape';
const LEFT_ARROW = 'ArrowLeft';
const RIGHT_ARROW = 'ArrowRight';

function setScroll(enabled) {
  const bodyScroll =
    document.scrollingElement || document.documentElement || document.querySelector('html');
  (bodyScroll as HTMLElement).style.overflow = enabled ? '' : 'hidden';
}

export class ImageViewer extends React.Component<ImageViewerProps> {
  componentDidUpdate() {
    setScroll(!this.props.showing);
  }

  componentDidMount() {
    setScroll(!this.props.showing);
  }

  componentWillUnmount() {
    setScroll(true);
  }

  handleNextClick = (event) => {
    event.stopPropagation();

    const {onImageSelect = NOOP, images, selected, infinite = true} = this.props;
    const indices = getIndices(images, selected, infinite);

    if (indices.nextImage) {
      onImageSelect(indices.nextImage as ShopScreenShot);
    }
  };

  handlePrevClick = (event) => {
    event.stopPropagation();

    const {onImageSelect = NOOP, images, selected, infinite = true} = this.props;
    const indices = getIndices(images, selected, infinite);

    if (indices.prevImage) {
      onImageSelect(indices.prevImage as ShopScreenShot);
    }
  };

  handleImageClick = (event) => event.stopPropagation();

  handleCloseClick = (event) => {
    event.stopPropagation();

    const {onClose = NOOP} = this.props;
    onClose();
  };

  handleKeyDown = (event) => {
    const {images, selected, onClose = NOOP, infinite = true, onImageSelect = NOOP} = this.props;

    const indices = getIndices(images, selected, infinite);

    switch (event.key) {
      case ESCAPE:
        onClose();
        break;
      case RIGHT_ARROW:
        onImageSelect(indices.nextImage as ShopScreenShot);
        break;
      case LEFT_ARROW:
        onImageSelect(indices.prevImage as ShopScreenShot);
        break;
    }
  };

  render() {
    const {
      images,
      showing = true,
      infinite = true,
      onClose = NOOP,
      selected,
      altPrefix = '',
    } = this.props;

    const indices = getIndices(images, selected, infinite);
    const showPrevPaddle = indices.prev !== null;
    const showNextPaddle = indices.next !== null;

    return (
      <FocusTrap active={showing}>
        {(focusRef) => (
          <aside
            ref={focusRef}
            onKeyDown={this.handleKeyDown}
            className={classNames(style.container, {[style.showing]: showing})}
          >
            {showPrevPaddle && (
              <section
                onClick={this.handlePrevClick}
                onKeyDown={this.handleKeyDown}
                className={classNames(style.paddle, {[style.hidden]: !infinite && indices.isFirst})}
              >
                <button type="button">
                  <Icon icon="chevronLeft" fill={ICON_FILL} size={ICON_SIZE} />
                </button>
              </section>
            )}
            <ul className={style.list}>
              {images.map((image, index) => (
                <button
                  type="button"
                  key={image.id}
                  onClick={this.handleCloseClick}
                  onKeyDown={this.handleCloseClick}
                  className={classNames(style.imageContainer, {
                    [style.active]: index === indices.active,
                    [style.prev]: index === indices.prev,
                    [style.next]: index === indices.next,
                  })}
                >
                  <Picture
                    alt={`${altPrefix} - #${index + 1}`}
                    imageUrl={image.url}
                    imageOptions={fullImageOptions}
                    imageClassName={style.image}
                  />
                  <span className={style.index}>{indices.displayIndex}</span>
                </button>
              ))}
            </ul>
            {showNextPaddle && (
              <section
                onClick={this.handleNextClick}
                onKeyDown={this.handleKeyDown}
                className={classNames(style.paddle, style.right, {
                  [style.hidden]: !infinite && indices.isLast,
                })}
              >
                <button type="button">
                  <Icon icon="chevronRight" fill={ICON_FILL} size={ICON_SIZE} />
                </button>
              </section>
            )}
            <button type="button" onClick={onClose} className={style.close}>
              <Icon icon="close" fill={ICON_FILL} size={ICON_SIZE} />
            </button>
          </aside>
        )}
      </FocusTrap>
    );
  }
}
